import * as Sentry from '@sentry/ember';

export type BackfillTypeName =
  | 'Segment'
  | 'DeleteSegment'
  | 'CollectionImport'
  | 'ConditionSchedule'
  | 'CampaignStop'
  | 'CampaignArchive'
  | 'CampaignBackfill'
  | 'CampaignDelete'
  | 'ActionBackfill'
  | 'ActionDelete'
  | 'NewsletterSend'
  | 'DeliveriesManage'
  | 'Export'
  | 'TriggeredBroadcast'
  | 'ProcessPerUserDataForBroadcast'
  | 'ImportPreprocess'
  | 'ImportProcess'
  | 'SQLImport'
  | 'ChangeRestartSettings'
  | 'DraftsMerge'
  | 'DeliveriesMerge'
  | 'Migration to enable only id to identify people'
  | 'Migration to enable email or id to identify people'
  | 'Identifier type migration';

export type BackfillType = {
  icon: string;
  label: string;
  status: string;
  objectType?:
    | 'segment'
    | 'campaign'
    | 'collection'
    | 'newsletter'
    | 'import'
    | 'export'
    | 'broadcast-trigger'
    | 'sql-import';
  overviewRoute?: string;
  detailRoute?: string;
  description?: string;
};

export const BackfillTypes: Record<BackfillTypeName, BackfillType> = {
  Segment: {
    icon: 'segment',
    label: 'Build Segment',
    status: 'Segment build in progress…',
    objectType: 'segment',
    detailRoute: 'segments.segment',
  },
  DeleteSegment: {
    icon: 'segment',
    label: 'Delete Segment',
    status: 'Segment delete in progress…',
    objectType: 'segment',
    detailRoute: 'segments.segment',
  },
  CollectionImport: {
    icon: 'import',
    label: 'Import Collection',
    status: 'Collection import in progress…',
    objectType: 'collection',
    detailRoute: 'collections.collection',
  },
  ConditionSchedule: {
    icon: 'segment',
    label: 'Schedule Condition',
    status: 'Segment optimization scheduling…',
    objectType: 'segment',
    detailRoute: 'segments.segment',
  },
  CampaignStop: {
    icon: 'campaigns',
    label: 'Stop Campaign',
    status: 'Stopping Campaign in progress…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  CampaignArchive: {
    icon: 'campaigns',
    label: 'Archive Campaign',
    status: 'Archiving Campaign in progress…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  CampaignBackfill: {
    icon: 'campaigns',
    label: 'Backfill Campaign',
    status: 'Including existing matches…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  CampaignDelete: {
    icon: 'campaigns',
    label: 'Delete Campaign',
    status: 'Campaign delete in progress…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  ActionBackfill: {
    icon: 'campaigns',
    label: 'Update Action',
    description: 'Updated time window or delay',
    status: 'Moving people out of the delay…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  ActionDelete: {
    icon: 'campaigns',
    label: 'Delete Action',
    description: 'Deleted an action',
    status: 'Action delete in progress…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
  },
  NewsletterSend: {
    icon: 'newsletter',
    label: 'Prepare Newsletter',
    status: 'Queuing deliveries…',
    objectType: 'newsletter',
    detailRoute: 'broadcasts.newsletter',
  },
  DeliveriesManage: {
    icon: 'email',
    label: 'Manage Deliveries',
    description: 'Processed bulk delivery sends or deletes',
    status: 'Processing bulk delivery actions…',
  },
  Export: {
    icon: 'export',
    label: 'Export Data',
    description: 'Exported people, metrics, or deliveries',
    objectType: 'export',
    status: 'Building export data…',
  },
  TriggeredBroadcast: {
    icon: 'api-triggered-broadcast',
    label: 'API Triggered Broadcast',
    status: 'Processing broadcast…',
    objectType: 'broadcast-trigger',
    detailRoute: 'broadcasts.broadcast.overview.broadcasts.trigger',
    overviewRoute: 'broadcasts.broadcast.overview',
  },
  ProcessPerUserDataForBroadcast: {
    icon: 'api-triggered-broadcast',
    label: 'API Triggered Broadcast Including Custom Data',
    status: 'Processing broadcast…',
    objectType: 'broadcast-trigger',
    detailRoute: 'broadcasts.broadcast.overview.broadcasts.trigger',
    overviewRoute: 'broadcasts.broadcast.overview',
  },
  ImportPreprocess: {
    icon: 'import',
    label: 'Import Preprocess',
    status: 'Import preprocessing in progress…',
    objectType: 'import',
    description: 'Uploaded import file',
  },
  ImportProcess: {
    icon: 'import',
    label: 'Import Process',
    status: 'Import in progress…',
    objectType: 'import',
    description: 'Imported CSV',
  },
  ChangeRestartSettings: {
    icon: 'campaigns',
    label: 'Change Campaign Frequency',
    status: 'Changing Campaign frequency settings…',
    objectType: 'campaign',
    detailRoute: 'campaigns.campaign',
    description: "Changed Campaign's frequency",
  },
  DraftsMerge: {
    icon: 'a-b-test',
    label: 'Prepare A/B Test',
    status: 'Preparing A/B test…',
    description: 'Created new A/B test',
  },
  DeliveriesMerge: {
    icon: 'a-b-test',
    label: 'Aggregate A/B Test results',
    status: 'Aggregating A/B test variant results…',
    description: 'Aggregated A/B test variant results',
  },
  SQLImport: {
    icon: 'database',
    label: 'SQL Sync',
    status: 'SQL Sync in progress…',
    description: 'Imported people',
    objectType: 'sql-import',
    detailRoute: 'sql-imports.import',
  },
  'Migration to enable only id to identify people': {
    icon: 'workspaces',
    label: 'Workspace migration (ID only)',
    status: 'Migration to enable only id to identify people…',
    description: 'Migration to enable only id to identify people',
  },
  'Migration to enable email or id to identify people': {
    icon: 'workspaces',
    label: 'Workspace migration (Email or ID)',
    status: 'Migration to enable email or id to identify people…',
    description: 'Migration to enable email or id to identify people',
  },
  'Identifier type migration': {
    icon: 'workspaces',
    label: 'Workspace migration (Identifier type)',
    status: 'Identifier type migration…',
    description: 'Identifier type migration',
  },
};

function fallback(type: string): BackfillType {
  // empty icon and description: by design, display only backfill type if it lacks full info object
  return {
    icon: '',
    label: type,
    description: '',
    status: 'Processing…',
  };
}

export default function backfillType(type: string): BackfillType {
  let typeInfo = BackfillTypes[type as BackfillTypeName];

  if (!typeInfo) {
    Sentry.captureException(new Error(`Undefined backfill type: ${type}. Add it to "app/utils/backfill-types.js".`));
  }

  return typeInfo || fallback(type);
}
